import { useState, useCallback, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { debounce } from "lodash"
import dayjs from "dayjs"
import { getCrewProfiles } from "store/crew-profiles"
import { getRoles } from "store/roles"
import { transformCrewProfiles } from "../utils/transformCrewData"

/**
 * Custom hook for managing crew member filtering and pagination with backend API
 * @param {Function} t - Translation function
 * @returns {Object} Filter states, data, and handler functions
 */
export const useCrewFilters = (t) => {
  const dispatch = useDispatch()

  // Redux state
  const { items: crewData, metadata, loading } = useSelector((state) => state.crewProfiles)
  const { items: rolesData, loading: rolesLoading } = useSelector((state) => state.roles)

  // Local filter state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedRoles, setSelectedRoles] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [selectedDate, setSelectedDate] = useState(null)
  const [sortBy, setSortBy] = useState("")
  const [sortDirection, setSortDirection] = useState("asc")

  // Fetch roles on mount
  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  // Debounced search handler (300ms delay)
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearch(value)
        setCurrentPage(1) // Reset to first page on search
      }, 300),
    []
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel()
    }
  }, [debouncedSetSearch])

  // Fetch crew profiles when filters change
  useEffect(() => {
    const roleIds = selectedRoles.length > 0 && !selectedRoles.includes("all") ? selectedRoles : []

    const status =
      selectedStatuses.length > 0 && !selectedStatuses.includes("all")
        ? selectedStatuses[0] // For now, take the first status. Backend supports single status
        : "all"

    // Format selected date for backend (YYYY-MM-DD)
    const availableOn = selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : undefined

    dispatch(
      getCrewProfiles({
        search: debouncedSearch,
        status,
        roleIds,
        page: currentPage,
        per_page: pageSize,
        sort_by: sortBy || undefined,
        sort_direction: sortDirection,
        available_on: availableOn
      })
    )
  }, [dispatch, debouncedSearch, selectedRoles, selectedStatuses, selectedDate, currentPage, pageSize, sortBy, sortDirection])

  // Transform backend data to table format
  const transformedCrewData = useMemo(() => transformCrewProfiles(crewData), [crewData])

  // Build role options from API data
  const roleOptions = useMemo(
    () => [
      { label: t("All") || "All", value: "all" },
      ...rolesData.map((role) => ({
        label: role.name,
        value: role.id
      }))
    ],
    [rolesData, t]
  )

  // Status filter options (keep the same as before)
  const statusOptions = useMemo(
    () => [
      { label: t("All") || "All", value: "all" },
      { label: t("Active") || "Active", value: "available" },
      { label: t("Deactivated") || "Deactivated", value: "disabled" }
    ],
    [t]
  )

  // Expand selected items when "all" is selected
  const expandedSelectedRoles = selectedRoles.includes("all")
    ? roleOptions.filter((r) => r.value !== "all").map((r) => r.value)
    : selectedRoles

  const expandedSelectedStatuses = selectedStatuses.includes("all")
    ? statusOptions.filter((s) => s.value !== "all").map((s) => s.value)
    : selectedStatuses

  const handleSearchChange = useCallback(
    (value) => {
      setSearch(value) // Update search input immediately for UI
      debouncedSetSearch(value) // Trigger debounced API call
    },
    [debouncedSetSearch]
  )

  const handleRoleChange = useCallback((roles) => {
    setSelectedRoles(roles)
    setCurrentPage(1) // Reset to first page on filter change
  }, [])

  const handleStatusChange = useCallback((statuses) => {
    setSelectedStatuses(statuses)
    setCurrentPage(1) // Reset to first page on filter change
  }, [])

  const handleDateChange = useCallback((date) => {
    setSelectedDate(date)
    setCurrentPage(1) // Reset to first page on filter change
  }, [])

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page)
  }, [])

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size)
    setCurrentPage(1)
  }, [])

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    // Handle sorting
    if (sorter?.columnKey) {
      // Map column keys to backend sort_by values
      const sortByMap = {
        name: "name",
        title: "title",
        role: "role",
        status: "status",
        assignedTo: "experiences",
        upcomingBookings: "upcoming",
        nextTimeOff: "next_time_off",
        availability: "availability"
      }

      const backendSortBy = sortByMap[sorter.columnKey]

      if (backendSortBy) {
        setSortBy(backendSortBy)
        setSortDirection(sorter.order === "descend" ? "desc" : "asc")
      }
    } else {
      // Clear sorting
      setSortBy("")
      setSortDirection("asc")
    }
  }, [])

  return {
    // State
    search,
    selectedRoles,
    selectedStatuses,
    selectedDate,
    currentPage,
    pageSize,
    sortBy,
    sortDirection,
    // Derived data
    roleOptions,
    statusOptions,
    expandedSelectedRoles,
    expandedSelectedStatuses,
    crewData: transformedCrewData,
    metadata,
    loading: loading || rolesLoading,
    // Handlers
    setSearch: handleSearchChange,
    setSelectedRoles: handleRoleChange,
    setSelectedStatuses: handleStatusChange,
    setSelectedDate: handleDateChange,
    setCurrentPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    handleTableChange
  }
}
